<template>
  <div class="design-picker" v-if="product">
    <div class="design-main-header" v-if="hasGroups || hasLegacy">
      <h2 class="design-main-title">Options</h2>
      <img class="design-arrow" src="@/assets/img/arrow_down.svg" alt="" />
    </div>
    <!-- New grouped format -->
    <template v-if="hasGroups">
      <div class="design-type" v-for="(group, gIdx) of designGroups" :key="`group-${gIdx}`">
        <div class="group-header">
          <h2>{{ group['acf-single-product-options-design-group-title'] }}</h2>
          <span class="group-right">
            <p class="group-hint">{{ group['acf-single-product-options-design-group-type'] === 'radio' ? 'Select one' : 'Select any that apply' }}</p>
            <span v-if="group['acf-single-product-options-design-group-type'] === 'radio'" class="group-indicator">
              <span class="indicator-dot orange"></span>
            </span>
            <span v-else class="group-indicator">
              <span class="indicator-dot green"></span>
              <span class="indicator-dot green overlap"></span>
            </span>
          </span>
        </div>
        <div class="design-card-container" :class="gridCols(group['acf-single-product-options-design-group-options']?.length || 0)">
          <template v-if="group['acf-single-product-options-design-group-type'] === 'radio'">
            <DesignSelectorRadio
              v-for="(design, idx) of group['acf-single-product-options-design-group-options']"
              :design="design"
              :group="group['acf-single-product-options-design-group-title']"
              :key="`group-${gIdx}-radio-${idx}`"
            />
          </template>
          <template v-else>
            <DesingSelectorCheck
              v-for="(design, idx) of group['acf-single-product-options-design-group-options']"
              :design="design"
              :group="group['acf-single-product-options-design-group-title']"
              :key="`group-${gIdx}-check-${idx}`"
            />
          </template>
        </div>
      </div>
    </template>
    <!-- Legacy flat format -->
    <template v-else-if="hasLegacy">
      <div class="design-type">
        <h2>Design Option</h2>
        <div class="design-card-container" :class="gridCols(product.acf['acf-single-product-options-design']?.length || 0)">
          <DesingSelectorCheck v-for="(design, idx) of product.acf['acf-single-product-options-design']" :design="design" :key="`design-${idx}`" />
        </div>
      </div>
    </template>
  </div>
</template>
<script setup>
import { computed } from 'vue'
import { ProductState } from './state'
import DesingSelectorCheck from './DesingSelectorCheck.vue'
import DesignSelectorRadio from './DesignSelectorRadio.vue'
const { product } = ProductState()

const designGroups = computed(() => product.value?.acf?.['acf-single-product-options-design-groups'] || [])
const hasGroups = computed(() => designGroups.value.length > 0)
const hasLegacy = computed(() => product.value?.acf?.['acf-single-product-options-design']?.length > 0)

const gridCols = (count) => {
  if (count <= 2) return 'cols-2'
  if (count === 4) return 'cols-2'
  if (count % 3 === 2) return 'cols-3-fill'  // e.g. 5,8,11 — last 2 stretch to fill
  return 'cols-3'
}
</script>
<style lang="scss" scoped>
  .design-picker {
    @apply ml-0 lg:ml-28;
    .design-main-header {
      @apply flex items-center gap-4 mt-8 mb-4;
      .design-main-title {
        @apply text-[28px] md:text-[32px] text-ore_green_dark;
      }
      .design-arrow {
        @apply w-5 h-5 md:w-6 md:h-6;
      }
    }
    .design-type {
      @apply bg-[#D9E5DD] rounded-2xl p-6 lg:p-8 mt-6 shadow-md;
      .group-header {
        @apply flex items-center justify-between mb-6;
        h2 {
          @apply text-[20px] text-ore_green_dark leading-tight mb-0;
        }
        .group-right {
          @apply flex items-center gap-2 flex-shrink-0;
          .group-hint {
            @apply text-sm text-ore_green_dark/40 italic;
          }
          .group-indicator {
            @apply flex items-center;
            .indicator-dot {
              @apply w-3 h-3 rounded-full;
              &.orange { @apply bg-ore_orange; }
              &.green { @apply bg-[#5A8A6A]; }
              &.overlap { @apply -ml-1 opacity-70; }
            }
          }
        }
      }
      h2 {
        @apply text-[20px] text-ore_green_dark mb-6;
      }
      .design-card-container {
        @apply grid grid-cols-2 gap-3;
        &.cols-3 { @apply lg:grid-cols-3; }
        &.cols-3-fill {
          @media (min-width: 1024px) {
            grid-template-columns: repeat(6, 1fr);
            & > :deep(*) { grid-column: span 2; }
            & > :deep(*:last-child:nth-child(3n+2)) { grid-column: span 3; }
            & > :deep(*:nth-last-child(2):nth-child(3n+1)) { grid-column: span 3; }
          }
        }
      }
    }
  }
</style>
